/**
 * OmegaCommanderPage — /omega/commander
 * Sprint 42 D1: unified command center. One screen aggregating the live state of
 * the autonomous factory — research teams, pipeline runs, marketing campaigns,
 * pending decisions — each tile a count + deep link. Read-only (no founder
 * signature); polls the existing admin GET endpoints. Drag-and-drop priority (D2)
 * and click-to-steer (D3) layer on top in follow-ups.
 */
import { Navigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { apiFetch } from '@/api/http';

function useCount<T>(key: string, path: string, extract: (data: T) => number) {
  return useQuery({
    queryKey: ['commander', key],
    queryFn: async () => {
      try {
        return extract(await apiFetch<T>(path));
      } catch {
        return null;
      }
    },
    refetchInterval: 15_000,
  });
}

function Tile({
  title,
  to,
  value,
  hint,
  accent,
}: {
  title: string;
  to: string;
  value: number | null | undefined;
  hint: string;
  accent: string;
}) {
  return (
    <Link
      to={to}
      style={{
        display: 'block',
        textDecoration: 'none',
        border: '1px solid #e5e7eb',
        borderLeft: `4px solid ${accent}`,
        borderRadius: 8,
        padding: '16px 18px',
        background: '#fff',
        color: '#111827',
        minWidth: 200,
      }}
    >
      <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6b7280' }}>
        {title}
      </div>
      <div style={{ fontSize: 30, fontWeight: 700, margin: '4px 0' }}>
        {value === undefined ? '…' : value === null ? '—' : value}
      </div>
      <div style={{ fontSize: 12, color: '#9ca3af' }}>{hint}</div>
    </Link>
  );
}

export function OmegaCommanderPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const pipeline = useCount<{ runs: unknown[] }>(
    'pipeline',
    '/admin/pipeline/runs',
    (d) => d.runs?.length ?? 0,
  );
  const research = useCount<unknown[]>(
    'research',
    '/admin/research/conductors',
    (d) => (Array.isArray(d) ? d.length : 0),
  );
  const running = useCount<Array<{ status?: string }>>(
    'research-running',
    '/admin/research/conductors',
    (d) => (Array.isArray(d) ? d.filter((r) => r.status === 'Running').length : 0),
  );
  // F3 — wire real counts into the marketing tile (was a placeholder).
  const marketing = useCount<{ total?: number; campaigns?: unknown[] }>(
    'marketing',
    '/admin/marketing/campaigns',
    (d) => d.total ?? d.campaigns?.length ?? 0,
  );
  const finance = useCount<{ total?: number; expenses?: unknown[] }>(
    'finance',
    '/finance/expenses',
    (d) => d.total ?? d.expenses?.length ?? 0,
  );
  const decisions = useCount<{ total?: number; decisions?: unknown[] }>(
    'decisions',
    '/omega/decisions/pending',
    (d) => d.total ?? d.decisions?.length ?? 0,
  );

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ marginBottom: 8 }}>
        <Link to="/omega" style={{ color: '#2563eb', fontSize: 14 }}>← OMEGA</Link>
      </div>
      <h1 style={{ fontSize: 26, marginBottom: 2 }}>Commander</h1>
      <p style={{ color: '#6b7280', marginTop: 0 }}>
        The autonomous factory at a glance. Polls every 15s.
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 16 }}>
        <Tile title="Pipeline runs" to="/omega/pipeline" value={pipeline.data} hint="active product runs" accent="#2563eb" />
        <Tile title="Research teams" to="/omega/research/teams" value={running.data} hint="running now" accent="#16a34a" />
        <Tile title="Research (total)" to="/omega/research/teams" value={research.data} hint="all conductors" accent="#16a34a" />
        <Tile title="Marketing" to="/omega/marketing" value={marketing.data} hint="campaigns" accent="#d97706" />
        <Tile title="Decisions" to="/omega/decisions" value={decisions.data} hint="pending →" accent="#dc2626" />
        <Tile title="Finance" to="/omega/finance" value={finance.data} hint="expense records" accent="#7c3aed" />
      </div>

      <div style={{ marginTop: 28, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Link to="/omega/founder-specs" style={{ fontSize: 14, color: '#2563eb' }}>+ New Founder spec (Mode B)</Link>
        <Link to="/omega/console" style={{ fontSize: 14, color: '#2563eb' }}>Console</Link>
        <Link to="/omega/briefing" style={{ fontSize: 14, color: '#2563eb' }}>Daily briefing</Link>
      </div>
    </div>
  );
}
